import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import './TournamentLobby.css';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TournamentLobby = ({ 
  user, 
  token, 
  onClose, 
  onJoinGame,
  addNotification 
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('browse'); // browse, create, my-tournaments, history
  const [tournaments, setTournaments] = useState([]);
  const [myTournaments, setMyTournaments] = useState({ hosted: [], participating: [], invitations: [] });
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Create tournament form state
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    max_participants: 4,
    variant: 'instant_king',
    move_timer: 30,
    visibility: 'public',
    registration_hours: 1,
    format: 'single_elimination',  // Phase 3: Tournament format
    swiss_rounds: null
  });

  // Fetch public tournaments
  const fetchTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tournaments?status=registration`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (data.success) {
        setTournaments(data.tournaments || []);
      }
    } catch (err) {
      setError(t('tournament.failedToLoadTournaments'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch user's tournaments
  const fetchMyTournaments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tournaments/my-tournaments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMyTournaments({
          hosted: data.hosted || [],
          participating: data.participating || [],
          invitations: data.invitations || []
        });
      } else {
        // Only set error if we're on the my-tournaments tab
      }
    } catch (err) {
      logger.warn('Failed to fetch my tournaments:', err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTournamentHistory = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/tournaments/history?limit=30`, { headers });
      const data = await response.json();
      if (data.success) {
        setTournamentHistory(data.history || []);
      }
    } catch (err) {
      logger.warn('Failed to fetch tournament history:', err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTournaments();
    if (token) {
      fetchMyTournaments();
    }
  }, [fetchTournaments, fetchMyTournaments, token]);

  // Create tournament
  const handleCreateTournament = async (e) => {
    e.preventDefault();
    if (!token) {
      setError(t('tournament.pleaseLoginToCreate'));
      return;
    }

    setLoading(true);
    try {
      const deadline = new Date(Date.now() + createForm.registration_hours * 60 * 60 * 1000).toISOString();
      
      const response = await fetch(`${API_URL}/api/tournaments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description,
          max_participants: createForm.max_participants,
          variant: createForm.variant,
          move_timer: createForm.move_timer,
          visibility: createForm.visibility,
          registration_deadline: deadline,
          format: createForm.format,  // Phase 3: Tournament format
          swiss_rounds: createForm.format === 'swiss' ? createForm.swiss_rounds : null
        })
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('success', `Tournament "${createForm.name}" created!`);
        setCreateForm({
          name: '',
          description: '',
          max_participants: 4,
          variant: 'instant_king',
          move_timer: 30,
          visibility: 'public',
          registration_hours: 1,
          format: 'single_elimination',
          swiss_rounds: null
        });
        setActiveTab('my-tournaments');
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToCreateTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToCreateTournament'));
    } finally {
      setLoading(false);
    }
  };

  // Join tournament
  const handleJoinTournament = async (tournamentId) => {
    if (!token) {
      setError(t('tournament.pleaseLoginToJoin'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('success', data.message);
        fetchTournaments();
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToJoinTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToJoinTournament'));
    }
  };

  // Leave tournament
  const handleLeaveTournament = async (tournamentId) => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('success', 'Left tournament');
        fetchMyTournaments();
        fetchTournaments();
      } else {
        setError(data.detail || t('tournament.failedToLeaveTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToLeaveTournament'));
    }
  };

  // Start tournament (host only)
  const handleStartTournament = async (tournamentId) => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('success', 'Tournament started!');
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToStartTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToStartTournament'));
    }
  };

  // Cancel tournament (host only)
  const handleCancelTournament = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to cancel this tournament?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('info', 'Tournament cancelled');
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToCancelTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToCancelTournament'));
    }
  };

  // Clear all finished tournaments
  const handleClearAllTournaments = async () => {
    if (!window.confirm('Clear all completed and cancelled tournaments from your history?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tournaments/clear-my-tournaments`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('success', data.message);
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToClearTournaments'));
      }
    } catch (err) {
      setError(t('tournament.failedToClearTournaments'));
    }
  };

  // Remove single tournament from history
  const handleRemoveFromHistory = async (tournamentId) => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/leave-history`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        addNotification?.('info', data.message);
        fetchMyTournaments();
      } else {
        setError(data.detail || t('tournament.failedToRemoveFromHistory'));
      }
    } catch (err) {
      setError(t('tournament.failedToRemoveFromHistory'));
    }
  };

  const getVariantLabel = (variant) => {
    const labels = {
      'classic': 'Classic Chess',
      'instant_king': 'Instant King',
      'fight_to_be_king': 'Fight to be King'
    };
    return labels[variant] || variant;
  };

  const getFormatLabel = (format) => {
    const labels = {
      'single_elimination': '🏆 Single Elimination',
      'double_elimination': '🔄 Double Elimination',
      'swiss': '🇨🇭 Swiss System'
    };
    return labels[format] || format;
  };

  const formatDeadline = (deadline) => {
    const date = new Date(deadline);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return 'Ended';
    if (diff < 60 * 60 * 1000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m left`;
    }
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      return `${hours}h left`;
    }
    return date.toLocaleDateString();
  };

  const renderTournamentCard = (tournament, showActions = true, isHostedSection = false) => {
    const isHost = tournament.host_id === user?.user_id;
    const isParticipant = tournament.participants?.some(p => p.user_id === user?.user_id);
    const isFull = tournament.participants?.length >= tournament.max_participants;
    const canStart = isHost && isFull && tournament.status === 'registration';
    const isFinished = ['completed', 'cancelled'].includes(tournament.status);

    return (
      <div key={tournament.tournament_id} className="tournament-card" data-testid={`tournament-${tournament.tournament_id}`}>
        <div className="tournament-header">
          <h3 className="tournament-name">{tournament.name}</h3>
          <span className={`tournament-status status-${tournament.status}`}>
            {tournament.status === 'registration' ? '📝 Registration' : 
             tournament.status === 'in_progress' ? '🎮 In Progress' : 
             tournament.status === 'completed' ? '🏆 Completed' : 
             tournament.status === 'cancelled' ? '❌ Cancelled' : tournament.status}
          </span>
        </div>
        
        {tournament.description && (
          <p className="tournament-description">{tournament.description}</p>
        )}
        
        <div className="tournament-details">
          <div className="detail-row">
            <span className="detail-icon">👥</span>
            <span>{tournament.participants?.length || 0}/{tournament.max_participants} players</span>
          </div>
          <div className="detail-row">
            <span className="detail-icon">🎯</span>
            <span>{getVariantLabel(tournament.variant)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-icon">📋</span>
            <span>{getFormatLabel(tournament.format || 'single_elimination')}</span>
          </div>
          <div className="detail-row">
            <span className="detail-icon">⏱️</span>
            <span>{tournament.move_timer ? `${tournament.move_timer}s/move` : 'Unlimited'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-icon">🔒</span>
            <span>{tournament.visibility === 'public' ? 'Public' : 'Invite Only'}</span>
          </div>
          {tournament.status === 'registration' && (
            <div className="detail-row">
              <span className="detail-icon">⏳</span>
              <span>{formatDeadline(tournament.registration_deadline)}</span>
            </div>
          )}
          {tournament.winner_name && (
            <div className="detail-row winner-row">
              <span className="detail-icon">🏆</span>
              <span>Winner: <strong>{tournament.winner_name}</strong></span>
            </div>
          )}
        </div>

        <div className="tournament-host">
          Hosted by: <strong>{tournament.host_name}</strong>
        </div>

        {showActions && (
          <div className="tournament-actions">
            {tournament.status === 'registration' && !isParticipant && !isFull && (
              <button 
                className="btn btn-primary"
                onClick={() => handleJoinTournament(tournament.tournament_id)}
                data-testid={`join-tournament-${tournament.tournament_id}`}
              >
                Join Tournament
              </button>
            )}
            {tournament.status === 'registration' && isParticipant && !isHost && (
              <button 
                className="btn btn-secondary"
                onClick={() => handleLeaveTournament(tournament.tournament_id)}
              >
                Leave
              </button>
            )}
            {canStart && (
              <button 
                className="btn btn-success"
                onClick={() => handleStartTournament(tournament.tournament_id)}
                data-testid={`start-tournament-${tournament.tournament_id}`}
              >
                🚀 Start Tournament
              </button>
            )}
            {isHost && tournament.status === 'registration' && (
              <button 
                className="btn btn-danger"
                onClick={() => handleCancelTournament(tournament.tournament_id)}
              >
                Cancel
              </button>
            )}
            {tournament.status === 'in_progress' && isParticipant && (
              <button 
                className="btn btn-primary"
                onClick={() => onJoinGame?.(tournament)}
                data-testid={`view-bracket-${tournament.tournament_id}`}
              >
                View Bracket
              </button>
            )}
            {/* Watch tournament matches as spectator */}
            {tournament.status === 'in_progress' && !isParticipant && (
              <button 
                className="btn btn-secondary"
                onClick={() => onJoinGame?.(tournament)}
                data-testid={`spectate-tournament-${tournament.tournament_id}`}
              >
                👁️ Watch
              </button>
            )}
            {isFull && !isParticipant && tournament.status === 'registration' && (
              <span className="tournament-full-badge">Tournament Full</span>
            )}
            {/* Remove from history button for finished tournaments */}
            {isFinished && (
              <button 
                className="btn btn-small btn-ghost"
                onClick={() => handleRemoveFromHistory(tournament.tournament_id)}
                title="Remove from history"
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tournament-lobby-overlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="tournament-lobby-modal" data-testid="tournament-lobby">
        <button className="close-btn" onClick={onClose} data-testid="close-tournament-lobby">×</button>
        
        <div className="tournament-lobby-header">
          <h2>🏆 {t('tournaments.title', 'Tournaments')}</h2>
        </div>

        <div className="tournament-tabs">
          <button 
            className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
            data-testid="tab-browse"
          >
            Browse
          </button>
          <button 
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
            data-testid="tab-create"
          >
            Create
          </button>
          <button 
            className={`tab-btn ${activeTab === 'my-tournaments' ? 'active' : ''}`}
            onClick={() => { setActiveTab('my-tournaments'); fetchMyTournaments(); }}
            data-testid="tab-my-tournaments"
          >
            My Tournaments
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => { setActiveTab('history'); fetchTournamentHistory(); }}
            data-testid="tab-history"
          >
            History
          </button>
        </div>

        {error && (
          <div className="tournament-error" onClick={() => setError(null)}>
            {error}
          </div>
        )}

        <div className="tournament-content">
          {/* Browse Tab */}
          {activeTab === 'browse' && (
            <div className="browse-tournaments">
              {loading ? (
                <div className="loading-spinner">Loading tournaments...</div>
              ) : tournaments.length === 0 ? (
                <div className="no-tournaments">
                  <p>No tournaments available right now.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('create')}>
                    Create One!
                  </button>
                </div>
              ) : (
                <div className="tournament-grid">
                  {tournaments.map(t => renderTournamentCard(t))}
                </div>
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="create-tournament">
              <form onSubmit={handleCreateTournament} className="create-form">
                <div className="form-group">
                  <label>Tournament Name</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Enter tournament name"
                    required
                    maxLength={50}
                    data-testid="tournament-name-input"
                  />
                </div>

                <div className="form-group">
                  <label>Description (optional)</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Describe your tournament..."
                    maxLength={200}
                    rows={2}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Players</label>
                    <select
                      value={createForm.max_participants}
                      onChange={(e) => setCreateForm({ ...createForm, max_participants: parseInt(e.target.value) })}
                      data-testid="tournament-players-select"
                    >
                      <option value={4}>4 Players</option>
                      <option value={8}>8 Players</option>
                      <option value={16}>16 Players</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Game Variant</label>
                    <select
                      value={createForm.variant}
                      onChange={(e) => setCreateForm({ ...createForm, variant: e.target.value })}
                      data-testid="tournament-variant-select"
                    >
                      <option value="instant_king">Instant King</option>
                      <option value="fight_to_be_king">Fight to be King</option>
                      <option value="classic">Classic Chess</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Move Timer</label>
                    <select
                      value={createForm.move_timer || ''}
                      onChange={(e) => setCreateForm({ ...createForm, move_timer: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      <option value={15}>15 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>60 seconds</option>
                      <option value="">Unlimited</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Visibility</label>
                    <select
                      value={createForm.visibility}
                      onChange={(e) => setCreateForm({ ...createForm, visibility: e.target.value })}
                      data-testid="tournament-visibility-select"
                    >
                      <option value="public">Public</option>
                      <option value="invite_only">Invite Only</option>
                    </select>
                  </div>
                </div>

                {/* Tournament Format Selection - Phase 3 */}
                <div className="form-group">
                  <label>Tournament Format</label>
                  <select
                    value={createForm.format}
                    onChange={(e) => setCreateForm({ ...createForm, format: e.target.value })}
                    data-testid="tournament-format-select"
                  >
                    <option value="single_elimination">🏆 Single Elimination</option>
                    <option value="double_elimination">🔄 Double Elimination</option>
                    <option value="swiss">🇨🇭 Swiss System</option>
                  </select>
                  <span className="form-hint">
                    {createForm.format === 'single_elimination' && 'One loss and you\'re out'}
                    {createForm.format === 'double_elimination' && 'Must lose twice to be eliminated'}
                    {createForm.format === 'swiss' && 'Everyone plays multiple rounds, points determine winner'}
                  </span>
                </div>

                {/* Swiss Rounds - only show for Swiss format */}
                {createForm.format === 'swiss' && (
                  <div className="form-group">
                    <label>Number of Rounds (Swiss)</label>
                    <select
                      value={createForm.swiss_rounds || ''}
                      onChange={(e) => setCreateForm({ ...createForm, swiss_rounds: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      <option value="">Auto (recommended)</option>
                      <option value={3}>3 Rounds</option>
                      <option value={4}>4 Rounds</option>
                      <option value={5}>5 Rounds</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Registration Period</label>
                  <select
                    value={createForm.registration_hours}
                    onChange={(e) => setCreateForm({ ...createForm, registration_hours: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 hour</option>
                    <option value={2}>2 hours</option>
                    <option value={6}>6 hours</option>
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-large"
                  disabled={loading || !createForm.name}
                  data-testid="create-tournament-btn"
                >
                  {loading ? 'Creating...' : '🏆 Create Tournament'}
                </button>
              </form>
            </div>
          )}

          {/* My Tournaments Tab */}
          {activeTab === 'my-tournaments' && (
            <div className="my-tournaments">
              {/* Clear All Button */}
              {(myTournaments.hosted.some(t => ['completed', 'cancelled'].includes(t.status)) ||
                myTournaments.participating.some(t => ['completed', 'cancelled'].includes(t.status))) && (
                <div className="clear-tournaments-header">
                  <button 
                    className="btn btn-danger btn-small"
                    onClick={handleClearAllTournaments}
                    data-testid="clear-all-tournaments-btn"
                  >
                    🗑️ Clear Finished Tournaments
                  </button>
                </div>
              )}
              
              {/* Invitations */}
              {myTournaments.invitations.length > 0 && (
                <div className="tournament-section">
                  <h3>📬 Invitations</h3>
                  <div className="tournament-grid">
                    {myTournaments.invitations.map(t => renderTournamentCard(t))}
                  </div>
                </div>
              )}

              {/* Hosted */}
              {myTournaments.hosted.length > 0 && (
                <div className="tournament-section">
                  <h3>🎯 Hosted by You</h3>
                  <div className="tournament-grid">
                    {myTournaments.hosted.map(t => renderTournamentCard(t, true, true))}
                  </div>
                </div>
              )}

              {/* Participating */}
              {myTournaments.participating.length > 0 && (
                <div className="tournament-section">
                  <h3>🎮 Participating</h3>
                  <div className="tournament-grid">
                    {myTournaments.participating.map(t => renderTournamentCard(t, true, false))}
                  </div>
                </div>
              )}

              {myTournaments.hosted.length === 0 && 
               myTournaments.participating.length === 0 && 
               myTournaments.invitations.length === 0 && (
                <div className="no-tournaments">
                  <p>You&apos;re not in any tournaments yet.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('browse')}>
                    Browse Tournaments
                  </button>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="tournament-history-tab" data-testid="tournament-history-tab">
              {loading ? (
                <div className="loading-spinner">Loading history...</div>
              ) : tournamentHistory.length === 0 ? (
                <div className="no-tournaments">
                  <p>No completed tournaments yet.</p>
                </div>
              ) : (
                <div className="tournament-history-list">
                  {tournamentHistory.map(t => (
                    <div key={t.tournament_id} className="history-card" data-testid={`history-card-${t.tournament_id}`}>
                      <div className="history-card-header">
                        <div className="history-card-title">
                          <span className="history-name">{t.name}</span>
                          <span className={`history-status history-status--${t.status}`}>
                            {t.status === 'completed' ? 'Completed' : 'Cancelled'}
                          </span>
                        </div>
                        <div className="history-meta">
                          <span className="history-variant">{t.variant?.replace(/_/g, ' ')}</span>
                          <span className="history-players">{t.participant_count}/{t.max_participants} players</span>
                          <span className="history-format">{t.format?.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                      {t.winner && (
                        <div className="history-result">
                          <div className="history-winner" data-testid={`history-winner-${t.tournament_id}`}>
                            <span className="trophy-icon">🏆</span>
                            <span className="winner-avatar">{t.winner.avatar || '👤'}</span>
                            <span className="winner-name">{t.winner.name}</span>
                          </div>
                          {t.runner_up && (
                            <div className="history-runner-up">
                              <span className="medal-icon">🥈</span>
                              <span className="runner-avatar">{t.runner_up.avatar || '👤'}</span>
                              <span className="runner-name">{t.runner_up.name}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="history-footer">
                        {t.completed_at && (
                          <span className="history-date">
                            {new Date(t.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                        {t.user_participated && (
                          <span className="history-participated-badge">You participated</span>
                        )}
                        <span className="history-host">Host: {t.host_name || 'Unknown'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentLobby;
